import { useRouter } from 'next/router';
import { type FC, useCallback, useEffect, useRef } from 'react';
import { useTrack } from 'src/contexts/AnalyticsContext/AnalyticsContext';
import { useLD } from 'src/contexts/LaunchDarklyContext/LaunchDarklyContext';
import { Events } from 'src/lib/trackingEvents';
import { contactRoutingConfig } from './config';
import {
  type ContactRoutingOutcome,
  useContactRoutingOutcome,
} from './use-contact-routing-outcome';

const { flag } = contactRoutingConfig;

export const useTrackContactRoutingOutcome = () => {
  const ld = useLD();
  const track = useTrack();

  return useCallback(
    (outcome: Exclude<ContactRoutingOutcome, { type: 'none' }>) => {
      if (outcome.type === 'exposed') {
        ld?.trackViewed(flag.key, outcome.arm);
        return;
      }
      track({
        type: Events.EXPERIMENT_EXCLUDED,
        payload: {
          experimentName: flag.key,
          experimentVariant: outcome.arm,
          reason: outcome.reason,
        },
      });
    },
    [ld, track],
  );
};

export const useReportContactRoutingOutcome = (
  arm: string,
  outcome: ContactRoutingOutcome,
) => {
  const trackOutcome = useTrackContactRoutingOutcome();
  const basePath = useRouter()?.basePath ?? '';
  const hasReported = useRef(false);

  useEffect(() => {
    if (outcome.type === 'none' || hasReported.current) {
      return;
    }
    hasReported.current = true;
    if (outcome.type === 'excluded' && arm === flag.variant) {
      const url = new URL(
        `${basePath}${flag.controlPagePath}`,
        window.location.origin,
      );
      url.search = window.location.search;
      url.searchParams.set(flag.optOutParam, 'off');
      window.location.replace(url.toString());
      return;
    }
    trackOutcome(outcome);
  });
};

export const ContactRoutingExposure: FC<{ arm: string }> = ({ arm }) => {
  useReportContactRoutingOutcome(arm, useContactRoutingOutcome(arm));
  return null;
};
